import 'dayjs/locale/pt-br';
import { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Spinner } from 'reactstrap';
import CustomHeader from '../../components/GridtableHeader/Header';
import ReportsPrint from './components/ReportsPrint';
import ReportsTableFooter from './components/ReportsTableFooter.jsx';
import ReportsTableHeader from './components/ReportsTableHeader.jsx';
import ReportsTableRow from './components/ReportsTableRow.jsx';
import './ReportsScreen.scss';
import { calculateReportTotals, calculateReportCounts } from '../../core/utils/osReport';

const statusOptions = [
  { id: 1, value: 'Aberta', label: 'Aberta' },
  { id: 2, value: 'Fechada', label: 'Fechada' },
  { id: 3, value: 'Quitada', label: 'Quitada' },
];

const ReportsScreen = ({ data, user, filter, setFilter, isLoading }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const printReportRef = useRef();
  const originalTitle = document.title;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Aberta':
        return '#6eb6ff';
      case 'Fechada':
        return '#eead2d';
      case 'Quitada':
        return '#00A86B';
      default:
        return '#b5b5b5';
    }
  };

  // Função de ordenação
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }
    setSortConfig({ key, direction });
  };

  // Aplica ordenação nos dados
  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key || !sortConfig.direction) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Se for a coluna de previsao_entrega ou created_at, converte para timestamp para comparação correta
    if (
      sortConfig.key === 'previsao_entrega' ||
      sortConfig.key === 'created_at'
    ) {
      // Converte DD/MM/YYYY para timestamp
      const parseDate = (dateStr) => {
        if (!dateStr) return 0;
        const parts = dateStr.split('/');
        if (parts.length !== 3) return 0;
        // new Date(ano, mês-1, dia)
        return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
      };

      aValue = parseDate(aValue);
      bValue = parseDate(bValue);
    }

    if (aValue === bValue) return 0;

    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totals = calculateReportTotals(sortedData);
  const counts = calculateReportCounts(sortedData);

  const handleDownloadPDF = useReactToPrint({
    content: () => printReportRef.current,
    documentTitle: `Relatorio-Ordens-de-Servico-${new Date()
      .toLocaleDateString('pt-BR')
      .replace(/\//g, '-')}`,
    pageStyle: `
      @page {
        size: A4 landscape;
        margin: 10mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,

    onBeforeGetContent: () => {
      return Promise.resolve();
    },

    onAfterPrint: () => {
      document.title = originalTitle;
    },
  });

  return (
    <div className="bodyshop-container">
      <div className="bodyshop-body default-table reports-container">
        <CustomHeader
          filter={filter}
          setFilter={setFilter}
          showConsultantFilter={false}
          statusOptions={statusOptions}
          onDownloadReport={handleDownloadPDF}
          isDownloadingReport={false}
          hasReportData={sortedData.length > 0}
        />

        <div className="reports-table-wrapper">
          <div className="reports-table">
            <ReportsTableHeader
              sortConfig={sortConfig}
              handleSort={handleSort}
            />

            <div className="reports-table-body">
              {isLoading ? (
                <div className="loading-container">
                  <Spinner color="primary" size="lg" />
                  <p>Carregando relatórios...</p>
                </div>
              ) : sortedData.length === 0 ? (
                <div className="no-results">
                  Nenhum resultado foi encontrado
                </div>
              ) : (
                sortedData.map((item, index) => (
                  <ReportsTableRow
                    key={item.id}
                    item={item}
                    index={index}
                    formatCurrency={formatCurrency}
                    getStatusColor={getStatusColor}
                  />
                ))
              )}
            </div>

            {!isLoading && data.length > 0 && (
              <ReportsTableFooter
                totals={totals}
                counts={counts}
                formatCurrency={formatCurrency}
              />
            )}
          </div>
        </div>

        {/* Componente de impressão - sempre renderizado mas escondido */}
        <div
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '1200px',
          }}
        >
          <ReportsPrint
            ref={printReportRef}
            data={sortedData}
            totals={totals}
            counts={counts}
            formatCurrency={formatCurrency}
            getStatusColor={getStatusColor}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportsScreen;
